import { Card, Carousel } from "@/components/ui/apple-cards-carousel";
import { blogs } from "@/lib/blogs";
import { ChevronDown, Grid, List } from "lucide-react";
import { useMemo, useState } from "react";
import "../../styles/ethosphere.css";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { SparklesCore } from "../ui/sparkles";

const Ethosphere = () => {
  const currentYear = new Date().getFullYear();
  const [selectedMonth, setSelectedMonth] = useState<string>("none");
  const [viewMode, setViewMode] = useState<"carousel" | "grid">("carousel");

  // Dynamically generate year options from blogs
  const yearOptions = useMemo(() => {
    const yearsFromBlogs = Array.from(
      new Set(blogs.map((blog) => blog.date.match(/\d{4}/)?.[0]))
    )
      .filter(Boolean)
      .sort((a, b) => Number(b) - Number(a)) as string[];

    // Always include current year if not present
    if (!yearsFromBlogs.includes(String(currentYear))) {
      yearsFromBlogs.unshift(String(currentYear));
    }

    return yearsFromBlogs.map((year) => ({ label: year, value: year }));
  }, [currentYear]);

  // Default to current year, or latest year with blogs if current year has none
  const [selectedYear, setSelectedYear] = useState<string>(() => {
    const currentYearStr = String(currentYear);
    const hasCurrentYearBlogs = blogs.some((blog) => blog.date.includes(currentYearStr));
    if (hasCurrentYearBlogs) {
      return currentYearStr;
    }
    // Fall back to the latest year that has blogs
    const latestYear = blogs
      .map((blog) => blog.date.match(/\d{4}/)?.[0])
      .filter(Boolean)
      .sort((a, b) => Number(b!) - Number(a!))[0];
    return latestYear || currentYearStr;
  });

  const monthOptions = [
    { label: "All", value: "none" },
    { label: "January", value: "January" },
    { label: "February", value: "February" },
    { label: "March", value: "March" },
    { label: "April", value: "April" },
    { label: "May", value: "May" },
    { label: "June", value: "June" },
    { label: "July", value: "July" },
    { label: "August", value: "August" },
    { label: "September", value: "September" },
    { label: "October", value: "October" },
    { label: "November", value: "November" },
    { label: "December", value: "December" },
  ];

  // Cap month options at the current month for the current year
  const currentMonth = new Date().getMonth() + 1; // 1-12
  const filteredMonthOptions =
    selectedYear === String(currentYear)
      ? monthOptions.filter((_, index) => index === 0 || index <= currentMonth) // index 0 is "All", index 1 is January, etc.
      : monthOptions;

  const filterBlogs = useMemo(() => {
    const filtered =
      selectedMonth === "none"
        ? blogs.filter((blog) => blog.date.includes(selectedYear))
        : blogs.filter(
          (blog) =>
            blog.date.includes(selectedYear) &&
            blog.date.includes(selectedMonth)
        );
    return filtered.reverse();
  }, [selectedYear, selectedMonth]);

  const blogCards = filterBlogs.map((blog, index) => (
    <Card
      key={blog.id}
      index={index}
      card={{
        src: blog.image,
        edition: blog.id,
        title: blog.heading,
        description: blog.description,
        date: blog.date,
        path: blog.path,
      }}
    />
  ));

  return (
    <div className="max-w-screen">
      <div className="h-[10rem] w-full flex flex-col items-center justify-center overflow-hidden rounded-md">
        <h2 className="text-5xl md:text-7xl pb-5 mt-30 font-poppins font-medium leading-tight tracking-wide text-[#57c0af] hover:text-[#57c0af]">
          Ethosphere
        </h2>
        <div className="w-[40rem] h-3 relative mb-4">
          {/* Gradients */}
          {/* <div className="absolute inset-x-20 top-0 bg-gradient-to-r from-transparent via-indigo-500 to-transparent h-[2px] w-3/4 blur-sm" />
          <div className="absolute inset-x-20 top-0 bg-gradient-to-r from-transparent via-indigo-500 to-transparent h-px w-3/4" />
          <div className="absolute inset-x-60 top-0 bg-gradient-to-r from-transparent via-sky-500 to-transparent h-[5px] w-1/4 blur-sm" />
          <div className="absolute inset-x-60 top-0 bg-gradient-to-r from-transparent via-sky-500 to-transparent h-px w-1/4" /> */}

          {/* Sparkles */}
          <SparklesCore
            background="transparent"
            minSize={0.4}
            maxSize={1}
            particleDensity={1200}
            className="w-full h-full"
            particleColor="#030F26"
          />
          <div className="absolute inset-0 w-full h-full [mask-image:radial-gradient(350px_200px_at_top,transparent_20%,white)]"></div>
          <p className="max-w-full mx-4 sm:mx-8 lg:mx-auto px-4 sm:px-8 lg:px-12 font-poppins text-center leading-relaxed text-secondary text-white text-sm sm:text-base lg:text-lg">
            The hub for NUQI's pioneering research and insights
          </p>
        </div>
      </div>

      <div className="flex flex-col justify-center items-center mt-20 mx-4">
        <div className="w-full overflow-x-none flex flex-row justify-center items-center gap-1 sm:gap-5 container mb-4">
          {/* Year Select */}
          <Select value={selectedYear} onValueChange={(value) => setSelectedYear(value)}>
            <SelectTrigger className="w-fit h-8">
              <SelectValue>
                <div className="flex flex-row rounded-2xl items-center hover:bg-[#57c0af]">
                  {selectedYear} <ChevronDown className="w-5 h-5" />
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="w-min bg-black text-white">
              {yearOptions.map(({ label, value }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Month Select */}
          <Select value={selectedMonth} onValueChange={(value) => setSelectedMonth(value)}>
            <SelectTrigger className="w-fit h-8 mr-2">
              <SelectValue>
                <div className="flex flex-row rounded-2xl items-center hover:bg-[#57c0af] text-nowrap">
                  {selectedMonth === "none" ? "All Month" : selectedMonth}{" "}
                  <ChevronDown className="w-5 h-5" />
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="w-min bg-black text-[#57c0af]">
              {filteredMonthOptions.map(({ label, value }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* View Toggle Buttons */}
          <div className="flex items-center justify-center space-x-4 ml-auto">
            <button
              onClick={() => setViewMode("grid")}
              className="p-2 lg:p-2 text-black bg-white rounded-md"
            >
              <Grid className="w-6 h-4 lg:h-6" />
            </button>
            <button
              onClick={() => setViewMode("carousel")}
              className="p-2 lg:p-2 text-black bg-white rounded-md"
            >
              <div className="text-white text-xs font-medium bg-black p-1 rounded-lg">
                <List className="w-6 h-4 lg:h-6" />
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        {viewMode === "carousel" ? (
          <Carousel items={blogCards} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {blogCards}
          </div>
        )}
      </div>
    </div>
  );
};

export default Ethosphere;
